import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { formatAgentOptions } from '../../../api/agents/formatters'
import agents from '../../../api/agents/hooks'
import { formatContactOptions } from '../../../api/contacts/formatters'
import { Owner } from '../../../api/globalTypes'
import { locationGeneralFieldsConfig } from '../../../api/locations/formValuesConfig'
import { formatPropertyProjectOptions } from '../../../api/properties/formatters'
import { propertyGeneralFieldsConfig } from '../../../api/properties/formValuesConfig'
import properties from '../../../api/properties/hooks'
import { ENTITY_TYPE } from '../../../common/constants'
import { realEstateGeneralFields } from '../../../common/forms/generalRealEstate/fields'
import { useLocationValues } from '../../../hooks/useLocationValues'
import { selectPropertyServiceFilter, selectPropertyTypeFilter } from '../../../store/formFilters'
import { mapValuesToFormFields } from '../../../utils/formUtils/forms'
import { splitPropertyValueFields } from '../utils'

const COST_TYPE_OPTIONS = [
    { value: '€/m2', label: '€/m2' },
    { value: '€', label: '€' },
]

export const usePropertyGeneralFormSelectOptions = (
    id?: string | number,
    propertyServiceId?: string | number,
    propertyTypeId?: string | number,
    owner?: Owner[]
) => {
    const { storePropertyServiceId, storePropertyTypeId } = useSelector((state) => ({
        storePropertyServiceId: selectPropertyServiceFilter(state),
        storePropertyTypeId: selectPropertyTypeFilter(state),
    }))

    // edit mode vs create mode
    const selectedPropertyServiceId = propertyServiceId || storePropertyServiceId
    const selectedPropertyTypeId = propertyTypeId || storePropertyTypeId

    const formFilters = useMemo(() => {
        if (!selectedPropertyServiceId || !selectedPropertyTypeId) return undefined

        return { propertyType: selectedPropertyTypeId, propertyService: selectedPropertyServiceId }
    }, [selectedPropertyTypeId, selectedPropertyServiceId])

    const { generalStaticFields, generalDynamicFields } = useMemo(() => splitPropertyValueFields(), [])

    const { data: staticFormValues, isPending: isStaticFormValuesPending } = properties.FormValues.useQuery(generalStaticFields)
    const { data: dynamicFormValues, isPending: isDynamicFormValuesPending } = properties.FormValues.useQuery(generalDynamicFields, formFilters, {
        enabled: Boolean(formFilters),
    })
    const { data: agentsData, isPending: isAgentsPending } = agents.GetAgents.useQuery()
    
    const { locationValues, isPending: isLocationValuesPending } = useLocationValues(ENTITY_TYPE.PROPERTY)

    const agentOptions = formatAgentOptions(agentsData)

    const propertyFormValues = useMemo(
        () => ({
            ...staticFormValues,
            ...dynamicFormValues,
        }),
        [staticFormValues, dynamicFormValues]
    )

    const propertySelectOptions = useMemo(() => {
        return mapValuesToFormFields(propertyFormValues, propertyGeneralFieldsConfig)
    }, [propertyFormValues])

    const locationSelectOptions = useMemo(() => {
        return mapValuesToFormFields(locationValues, locationGeneralFieldsConfig)
    }, [locationValues])

    const selectOptions = useMemo(
        () => ({
            ...propertySelectOptions,
            ...locationSelectOptions,
            [realEstateGeneralFields.PRIMARY_AGENT_ID]: agentOptions,
            [realEstateGeneralFields.AGENT_ID]: agentOptions,
            [realEstateGeneralFields.PROJECT_ID]: formatPropertyProjectOptions(id),
            [realEstateGeneralFields.OWNER_ID]: formatContactOptions(owner),
            [realEstateGeneralFields.MAINTENANCE_COSTS_TYPE]: COST_TYPE_OPTIONS,
            [realEstateGeneralFields.COMMON_MAINTENANCE_COSTS_TYPE]: COST_TYPE_OPTIONS,
            [realEstateGeneralFields.COMMON_UTILITY_COSTS_TYPE]: COST_TYPE_OPTIONS,
        }),
        [propertySelectOptions, locationSelectOptions, agentOptions, id, owner]
    )

    return {
        selectOptions,
        isPending: isStaticFormValuesPending || (Boolean(formFilters) && isDynamicFormValuesPending) || isAgentsPending || isLocationValuesPending,
    }
}
